import { faBug, faThumbsUp } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Typography } from "@mui/material"
import { alpha, Theme } from "@mui/system"
import { historySVC } from "app/history/service"
import { translate } from "app/language/service"
import { LOGIN_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React, { useState } from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { Logo } from "ui/components/Logo"
import { getUiConfigLoginImageSrc } from "ui/configuration/tools/common"
import { MAX_TABLET_WIDTH_IN_PX } from "ui/model"
import { IUser, SIGN_UP_VERIFICATION_PAGE_ID } from "user/model"
import { activateAdstandingAccount } from "user/tools/signup"
import { LoginPageShiningSquares } from "../LoginPageShiningSquares"

export const SignUpVerificationPage = () => {
  const [user, setUser] = useState<IUser | null>(null)
  const [hasError, setHasError] = useState(false)
  React.useEffect(() => {
    const code = historySVC.getHashParameter("code")
    if (!code) {
      setHasError(true)
      return
    }
    const email = historySVC.getHashParameter("email")
    if (!email) {
      setHasError(true)
      return
    }
    activateAdstandingAccount(email, code)
      .then(user => {
        setUser(user)
      })
      .catch(error => {
        console.error(error)
        setHasError(true)
      })
  }, [])
  const backgroundSx: any = {}
  const imageSrc = getUiConfigLoginImageSrc()
  if (imageSrc) {
    backgroundSx.backgroundImage = `url(${imageSrc})`
    backgroundSx.backgroundRepeat = "no-repeat"
    backgroundSx.backgroundPosition = "center"
    backgroundSx.backgroundSize = "cover"
  } else {
    backgroundSx.backgroundColor = (theme: Theme) => theme.palette.primary.main
    backgroundSx.background = (theme: Theme) =>
      `linear-gradient(90deg, ${alpha(theme.palette.primary.main as string, 0.75)} 0%, ${
        theme.palette.primary.main
      } 100%)`
  }
  return (
    <FlexBox
      id={SIGN_UP_VERIFICATION_PAGE_ID}
      vertical
      fullWidth
      sx={{
        "position": "relative",
        "flexDirection": "column",
        "height": "100%",
        "padding": 0,
        "margin": 0,
        "boxShadow": "0 0.125rem 0.5rem 0 rgba(0, 0, 0, 0.2)",
        "backgroundColor": theme => theme.palette.background.paper,
        "& .code-version": {
          marginBottom: "1rem"
        }
      }}>
      {!imageSrc && <LoginPageShiningSquares />}
      <FlexBox
        sx={{
          ...backgroundSx,
          height: "calc(100% - 10.5rem)",
          minHeight: "calc(100% - 10.5rem)",
          maxHeight: "calc(100% - 10.5rem)",
          width: "calc(100% - 10.5rem)",
          minWidth: "calc(100% - 10.5rem)",
          maxWidth: "calc(100% - 10.5rem)",
          overflow: "hidden",
          [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%"
          }
        }}
        id={`${SIGN_UP_VERIFICATION_PAGE_ID}-background`}>
        <FlexBox
          gap=""
          vertical
          jc="space-evenly"
          sx={{
            "backgroundColor": theme => theme.palette.background.default,
            "borderRadius": ".375rem",
            "p": "2rem",
            "width": "60%",
            "minWidth": "60%",
            "maxWidth": "60%",
            "height": "calc(100% - 2rem)",
            "minHeight": "calc(100% - 2rem)",
            "maxHeight": "calc(100% - 2rem)",
            "pl": "3rem",
            "pr": "3rem",
            "zIndex": "1",
            "& .MuiFormControl-root": {
              borderRadius: ".375rem"
            },
            "& input": {
              px: ".4rem"
            },
            [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%"
            }
          }}
          id={`${SIGN_UP_VERIFICATION_PAGE_ID}-form`}>
          <Logo id={`${SIGN_UP_VERIFICATION_PAGE_ID}-logo`} sx={{ minHeight: "5rem" }} />
          {hasError ? (
            <FlexBox vertical gap sx={{ p: "2rem" }}>
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
                <FontAwesomeIcon icon={faBug} size="3x" className="icon" />
                <Typography color="error">{translate("user.signup.error")}</Typography>
              </FlexBox>
              <Button onClick={() => navigate(LOGIN_PATH)} variant="contained">
                {translate("login.title")}
              </Button>
            </FlexBox>
          ) : !user ? (
            <LinearLoader />
          ) : (
            <FlexBox vertical gap sx={{ p: "2rem" }}>
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
                <FontAwesomeIcon icon={faThumbsUp} size="3x" className="icon" />
                <Typography id="signup-verification-success-message" color="textPrimary">
                  {translate("user.signup.confirmed", [user.firstname || "-", user.lastname || "-"])}
                </Typography>
              </FlexBox>
              <Button id="signup-verification-login-button" onClick={() => navigate(LOGIN_PATH)} variant="contained">
                {translate("login.title")}
              </Button>
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}
